// 左侧边栏组件，用于管理工作表
import React, {useRef, useState, useEffect} from 'react';
import {
  Layout,
  Input,
  Divider,
  Collapse,
  Button,
  Popover,
  Card,
  InputRef
} from 'antd';
import {
  MenuFoldOutlined,
  FileDoneOutlined,
  EllipsisOutlined,
  FileAddOutlined,
  LockOutlined,
  CaretRightOutlined,
  CheckOutlined,
  EditOutlined,
  CopyOutlined,
  ExportOutlined,
  ImportOutlined,
  DeleteOutlined,
  PieChartOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import {SheetInfo, CardPosition, DashboardInfo} from '../types';
import '../style/index.css';

const {Sider} = Layout;
const {Search} = Input;
const {Panel} = Collapse;

interface SiderPanelProps {
  height?: string | number;
  siderCollapsed: boolean;
  onToggleSider: () => void;
  onSearchSheets: (value: string) => void;
  filterSheetList: SheetInfo[];
  selectedCollection: string;
  focusCollection: string;
  isRenameSheet: boolean;
  onSelectSheet: (collection: string) => void;
  onRenameConfirm: (
    e:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
    collection: string
  ) => void;
  onOperateSheet: (
    e: React.MouseEvent<HTMLElement>,
    collection: string
  ) => void;
  onCreateSheet: () => void;
  isSheetCardVisible: boolean;
  cardPosition: CardPosition;
  sheetCardRef: React.RefObject<HTMLDivElement>;
  renameInputRef: React.RefObject<HTMLDivElement>;
  focusSheetCreateUserId: string;
  userId: string;
  focusSheetPermissions: string[];
  onGetSheetPermissions: () => void;
  onSetSheetPermissions: (type: string) => void;
  onRenameSheet: () => void;
  onCreateCopySheet: () => void;
  onExportSheetAsExcel: () => void;
  onExportSheetAsCsv: () => void;
  onImportExcelFile: () => void;
  onDeleteSheet: () => void;
  onCreateDashboard: (type: string) => void;
  isDashboardCardVisible: boolean;
  dashboardCardRef: React.RefObject<HTMLDivElement>;
  filterDashboardList: DashboardInfo[];
  selectedDashboard: number | null;
  focusDashboard: number | null;
  focusDashboardInfo: DashboardInfo | null;
  onSelectDashboard: (id: number) => void;
  onOperateDashboard: (
    e: React.MouseEvent<HTMLElement>,
    dashboard: DashboardInfo
  ) => void;
  onDeleteDashboard: (dashboardId: number) => void;
  onRenameDashboard: () => void;
  isRenameDashboard: boolean;
  onRenameDashboardConfirm: (
    e:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
    dashboard: DashboardInfo
  ) => void;
  onSetDashboardPermissions: (permission: string) => void;
}

export const SiderPanel: React.FC<SiderPanelProps> = props => {
  const {
    height,
    siderCollapsed,
    onToggleSider,
    onSearchSheets,
    filterSheetList,
    selectedCollection,
    focusCollection,
    isRenameSheet,
    onSelectSheet,
    onRenameConfirm,
    onOperateSheet,
    onCreateSheet,
    isSheetCardVisible,
    cardPosition,
    sheetCardRef,
    renameInputRef,
    focusSheetCreateUserId,
    userId,
    focusSheetPermissions,
    onGetSheetPermissions,
    onSetSheetPermissions,
    onRenameSheet,
    onCreateCopySheet,
    onExportSheetAsExcel,
    onExportSheetAsCsv,
    onImportExcelFile,
    onDeleteSheet,
    onCreateDashboard,
    filterDashboardList,
    selectedDashboard,
    focusDashboard,
    focusDashboardInfo,
    isDashboardCardVisible,
    dashboardCardRef,
    onSelectDashboard,
    onRenameDashboardConfirm,
    onDeleteDashboard,
    onOperateDashboard,
    isRenameDashboard,
    onRenameDashboard,
    onSetDashboardPermissions
  } = props;

  const searchRef = useRef<InputRef>(null);
  const [isCreateExpanded, setIsCreateExpanded] = useState(true);
  const [isSheetExpanded, setIsSheetExpanded] = useState(true);
  const [isDashboardExpanded, setIsDashboardExpanded] = useState(true);

  // 添加权限相关状态
  const [isPermissionMenuVisible, setIsPermissionMenuVisible] = useState(false);
  const [isPermissionDetailVisible, setIsPermissionDetailVisible] =
    useState(false);
  const permissionMenuRef = useRef<HTMLDivElement>(null);
  const permissionDetailRef = useRef<HTMLDivElement>(null);
  const [permissionOptions, setPermissionOptions] = useState<any>([]);
  const [activeView, setActiveView] = useState<'sheet' | 'dashboard'>('sheet');

  // Diretus权限选项配置
  const apiPermissionOptions: any[] = [
    {
      value: 'all',
      label: '全部权限',
      description: '可查看、编辑、管理视图的所有内容和设置',
      required: ['create', 'read', 'update', 'delete']
    },
    {
      value: 'edit',
      label: '可编辑',
      description: '可查看和编辑视图内容，不能修改视图设置',
      required: ['read', 'update']
    },
    {
      value: 'read',
      label: '仅浏览',
      description: '只能查看视图内容，不能进行任何操作',
      required: ['read']
    },
    {
      value: 'none',
      label: '无权限',
      description: '无法访问此视图的任何内容',
      required: []
    }
  ];

  // 自定义权限表
  const customPermissionOptions: any[] = [
    {
      value: 'ALL',
      label: '全部权限',
      description: '可查看、编辑、管理视图的所有内容和设置',
    },
    {
      value: 'READ',
      label: '仅浏览',
      description: '只能查看视图内容，不能进行任何操作',
    },
    {
      value: 'NONE',
      label: '无权限',
      description: '无法访问此视图的任何内容',
    }
  ];

  // 卡片位置状态
  const [permissionMenuPosition, setPermissionMenuPosition] = useState({
    top: 0,
    left: 0
  });
  const [permissionDetailPosition, setPermissionDetailPosition] = useState({
    top: 0,
    left: 0
  });

  // 鼠标悬浮状态
  const [isMouseOverPermissionButton, setIsMouseOverPermissionButton] =
    useState(false);
  const [isMouseOverPermissionMenu, setIsMouseOverPermissionMenu] =
    useState(false);
  const [isMouseOverPermissionDetail, setIsMouseOverPermissionDetail] =
    useState(false);

  // 处理权限设置点击 - 显示权限功能菜单
  const handlePermissionClick = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    console.log('权限设置被点击');

    // 计算权限功能菜单位置（在工作表卡片右侧）
    if (sheetCardRef.current) {
      const rect = sheetCardRef.current.getBoundingClientRect();
      setPermissionMenuPosition({
        top: rect.top,
        left: rect.right - 20
      });
    }

    setIsPermissionMenuVisible(true);
    if(type === 'sheet') {
      setActiveView('sheet');
      onGetSheetPermissions();
      setPermissionOptions(apiPermissionOptions);
    }else{
      setActiveView('dashboard');
      setPermissionOptions(customPermissionOptions);
    }
  };

  // 设置权限
  const onSetPermissionClick = async (e: React.MouseEvent, permission: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsPermissionMenuVisible(false);
    setIsPermissionDetailVisible(false);
    setIsMouseOverPermissionDetail(false);
    if(activeView === 'sheet') {
      onSetSheetPermissions(permission);
    }else{
      onSetDashboardPermissions(permission);
    }
  }

  // 处理设置全员权限悬浮
  const handleAllPermissionHover = (e: React.MouseEvent) => {
    // 计算权限详情卡片位置（在权限功能菜单右侧）
    if (permissionMenuRef.current) {
      const rect = permissionMenuRef.current.getBoundingClientRect();
      setPermissionDetailPosition({
        top: rect.top,
        left: rect.right - 20
      });
    }
    setIsPermissionDetailVisible(true);
  };

  // 处理设置全员权限离开悬浮
  const handleAllPermissionLeave = () => {
    // 延迟隐藏，给用户时间移动到详情卡片
    setTimeout(() => {
      // 检查鼠标是否移动到权限详情卡片上
      if (!isMouseOverPermissionDetail) {
        setIsPermissionDetailVisible(false);
      }
    }, 100);
  };

  // 全局点击事件处理，隐藏所有卡片
  const handleGlobalClick = (e: MouseEvent) => {
    // 检查点击是否在任何卡片内
    const isInSheetCard = sheetCardRef.current?.contains(e.target as Node);
    const isInPermissionMenu = permissionMenuRef.current?.contains(
      e.target as Node
    );
    const isInPermissionDetail = permissionDetailRef.current?.contains(
      e.target as Node
    );
    const isInDashboardCard = dashboardCardRef.current?.contains(
      e.target as Node
    );

    // 如果点击在任何卡片内，则不执行隐藏操作
    if (
      isInSheetCard ||
      isInPermissionMenu ||
      isInPermissionDetail ||
      isInDashboardCard
    ) {
      return;
    }

    // 只有点击在卡片外时才隐藏卡片
    setIsPermissionMenuVisible(false);
    setIsPermissionDetailVisible(false);
  };

  // 添加全局点击监听
  useEffect(() => {
    document.addEventListener('mousedown', handleGlobalClick);
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, []);

  return (
    <>
      <Sider
        style={{height}}
        theme="light"
        className="my-sider"
        collapsed={siderCollapsed}
        collapsedWidth={0}
        width='250px'
      >
        {/* Sider内容保持不变 */}
        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          {!siderCollapsed && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 0',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <MenuFoldOutlined
                className="header-icon"
                onClick={onToggleSider}
              />
              <Divider type="vertical" />
              <Search
                ref={searchRef}
                placeholder="搜索"
                onSearch={onSearchSheets}
                className="rounded-search-input" // 添加自定义样式类
                style={{width: '80%'}}
                allowClear
              />
            </div>
          )}
          <div
            style={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* 工作表部分 */}
            <div
              className="sheet-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: isSheetExpanded ? 1 : 0,
                flexShrink: 1,
                minHeight: 0
              }}
            >
              <Divider style={{margin: '0'}} />
              <div
                style={{
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setIsSheetExpanded(!isSheetExpanded)}
              >
                <span style={{color: '#999'}}>工作表</span>
                {isSheetExpanded ? (
                  <DownOutlined style={{color: '#999'}} />
                ) : (
                  <UpOutlined style={{color: '#999'}} />
                )}
              </div>
              {isSheetExpanded && (
                <div
                  className="sheet-list"
                  style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                  }}
                >
                  {filterSheetList.map(sheet => (
                    <div
                      key={sheet.collection}
                      className="sheet-item"
                      onClick={() => onSelectSheet(sheet.collection)}
                      style={{
                        backgroundColor:
                          selectedCollection === sheet.collection
                            ? '#D8EFE0'
                            : focusCollection === sheet.collection
                            ? '#F2F3F3'
                            : undefined,
                        color:
                          selectedCollection === sheet.collection
                            ? '#00B700'
                            : undefined,
                        fontWeight:
                          selectedCollection === sheet.collection
                            ? 'bold'
                            : undefined,
                        boxShadow:
                          selectedCollection === sheet.collection
                            ? '0px 0px 17px -9px rgba(0, 113, 109, 0.49)'
                            : undefined,
                      }}
                    >
                      <FileDoneOutlined
                        style={{
                          marginRight: '5px',
                          color:
                            selectedCollection === sheet.collection
                              ? '#00B700'
                              : undefined,
                          fontWeight:
                            selectedCollection === sheet.collection
                              ? 'bold'
                              : undefined
                        }}
                      />
                      <span
                        className="ellipsis-btn"
                        style={{maxWidth: '150px', display: 'inline-block'}}
                      >
                        {isRenameSheet &&
                        focusCollection === sheet.collection ? (
                          <div
                            ref={renameInputRef}
                            onClick={e => e.stopPropagation()}
                          >
                            <Input
                              defaultValue={sheet.name}
                              onBlur={e => onRenameConfirm(e, sheet.collection)}
                              onPressEnter={e =>
                                onRenameConfirm(e, sheet.collection)
                              }
                              autoFocus
                            />
                          </div>
                        ) : (
                          sheet.name
                        )}
                      </span>
                      <span
                        className="operation-btn"
                        onClick={e => onOperateSheet(e, sheet.collection)}
                        style={{
                          backgroundColor:
                            focusCollection === sheet.collection
                              ? '#E6E8E8'
                              : undefined
                        }}
                      >
                        <EllipsisOutlined />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 仪表盘部分 */}
            <div
              className="dashboard-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: isDashboardExpanded ? 1 : 0,
                flexShrink: 1,
                minHeight: 0
              }}
            >
              <Divider style={{margin: '0'}} />
              <div
                style={{
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setIsDashboardExpanded(!isDashboardExpanded)}
              >
                <span style={{color: '#999'}}>仪表盘</span>
                {isDashboardExpanded ? (
                  <DownOutlined style={{color: '#999'}} />
                ) : (
                  <UpOutlined style={{color: '#999'}} />
                )}
              </div>
              {isDashboardExpanded && (
                <div
                  className="dashboard-list"
                  style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                  }}
                >
                  {filterDashboardList.map(dashboard => (
                    <div
                      key={dashboard.id}
                      className="sheet-item"
                      onClick={() => onSelectDashboard(dashboard.id)}
                      style={{
                        backgroundColor:
                          selectedDashboard === dashboard.id
                            ? '#D8EFE0'
                            : focusDashboard === dashboard.id
                            ? '#F2F3F3'
                            : undefined,
                        color:
                          selectedDashboard === dashboard.id
                            ? '#00B700'
                            : undefined,
                        fontWeight:
                          selectedDashboard === dashboard.id
                            ? 'bold'
                            : undefined,
                        boxShadow:
                          selectedDashboard === dashboard.id
                            ? '0px 0px 17px -9px rgba(0, 113, 109, 0.49)'
                            : undefined,
                      }}
                    >
                      <PieChartOutlined
                        style={{
                          marginRight: '5px',
                          color:
                            selectedDashboard === dashboard.id
                              ? '#00B700'
                              : undefined,
                          fontWeight:
                            selectedDashboard === dashboard.id
                              ? 'bold'
                              : undefined
                        }}
                      />
                      <span
                        className="ellipsis-btn"
                        style={{maxWidth: '150px', display: 'inline-block'}}
                      >
                        {isRenameDashboard &&
                        focusDashboard === dashboard.id ? (
                          <div
                            ref={renameInputRef}
                            onClick={e => e.stopPropagation()}
                          >
                            <Input
                              defaultValue={dashboard.name}
                              onBlur={e =>
                                onRenameDashboardConfirm(e, dashboard)
                              }
                              onPressEnter={e =>
                                onRenameDashboardConfirm(e, dashboard)
                              }
                              autoFocus
                            />
                          </div>
                        ) : (
                          dashboard.name
                        )}
                      </span>
                      {dashboard?.createUserId === userId && (
                        <span
                          className="operation-btn"
                          onClick={e => onOperateDashboard(e, dashboard)}
                          style={{
                            backgroundColor:
                              focusDashboard === dashboard.id
                                ? '#E6E8E8'
                                : undefined
                          }}
                        >
                          <EllipsisOutlined />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 新建区域 */}
          <div className="create-container" style={{flexShrink: 0}}>
            <Divider style={{margin: '0'}} />
            <div
              style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setIsCreateExpanded(!isCreateExpanded)}
            >
              <span style={{color: '#999'}}>新建</span>
              {isCreateExpanded ? (
                <DownOutlined style={{color: '#999'}} />
              ) : (
                <UpOutlined style={{color: '#999'}} />
              )}
            </div>
            {isCreateExpanded && (
              <div>
                <Button
                  className='sheet-item'
                  type="text"
                  icon={<FileAddOutlined />}
                  style={{width: '100%', textAlign: 'left', margin: '8px'}}
                  onClick={onCreateSheet}
                >
                  工作表
                </Button>
                <Button
                  className='sheet-item'
                  type="text"
                  icon={<PieChartOutlined />}
                  style={{width: '100%', textAlign: 'left', margin: '8px'}}
                  onClick={() => onCreateDashboard('common')}
                >
                  仪表盘
                </Button>
              </div>
            )}
          </div>
        </div>
      </Sider>

      {/* 工作表操作卡片 */}
      {isSheetCardVisible && (
        <div
          ref={sheetCardRef}
          style={{
            position: 'absolute',
            top: cardPosition.top + 'px',
            left: cardPosition.left + 'px',
            zIndex: 99999,
            width: '200px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #d9d9d9',
            padding: '8px 0'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* 权限设置 - 添加向右三角形图标 */}
          {focusSheetCreateUserId === userId && (
            <div
              data-permission-button="true"
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#333'
              }}
              onClick={(e) => handlePermissionClick(e, 'sheet')}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <LockOutlined style={{marginRight: '8px'}} />
                设置工作表权限
              </div>
              <CaretRightOutlined style={{color: '#999', fontSize: '12px'}} />
            </div>
          )}

          {/* 其他操作项 */}
          {focusSheetCreateUserId === userId && (
            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#333'
              }}
              onClick={e => {
                e.stopPropagation();
                onRenameSheet();
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <EditOutlined style={{marginRight: '8px'}} />
              重命名
            </div>
          )}

          {focusSheetCreateUserId === userId &&
            focusCollection === selectedCollection && (
              <div
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#333'
                }}
                onClick={e => {
                  e.stopPropagation();
                  console.log('创建副本被点击');
                  onCreateCopySheet();
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CopyOutlined style={{marginRight: '8px'}} />
                创建副本
              </div>
            )}

          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#333'
            }}
            onClick={e => {
              e.stopPropagation();
              console.log('导出Excel被点击');
              onExportSheetAsExcel();
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <ExportOutlined style={{marginRight: '8px'}} />
            导出Excel文件
          </div>

          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#333'
            }}
            onClick={e => {
              e.stopPropagation();
              console.log('导出CSV被点击');
              onExportSheetAsCsv();
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <ExportOutlined style={{marginRight: '8px'}} />
            导出Csv文件
          </div>

          {focusSheetCreateUserId === userId &&
            focusCollection === selectedCollection && (
              <div
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#333'
                }}
                onClick={e => {
                  e.stopPropagation();
                  console.log('导入Excel被点击');
                  onImportExcelFile();
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <ImportOutlined style={{marginRight: '8px'}} />
                导入Excel文件
              </div>
            )}

          {focusSheetCreateUserId === userId && (
            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#ff4d4f',
                borderTop: '1px solid #f0f0f0',
                marginTop: '4px'
              }}
              onClick={e => {
                e.stopPropagation();
                console.log('删除工作表被点击');
                onDeleteSheet();
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#fff2f0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <DeleteOutlined style={{marginRight: '8px'}} />
              删除
            </div>
          )}
        </div>
      )}

      {/* 权限功能菜单卡片 */}
      {isPermissionMenuVisible && (
        <div
          ref={permissionMenuRef}
          data-permission-menu="true"
          style={{
            position: 'fixed',
            top: cardPosition.top + 'px',
            left: permissionMenuPosition.left + 'px',
            zIndex: 100000,
            width: '220px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #d9d9d9',
            padding: '8px 0'
          }}
          onClick={e => e.stopPropagation()}
          onMouseEnter={() => {
            setIsMouseOverPermissionMenu(true);
          }}
          onMouseLeave={() => {
            setIsMouseOverPermissionMenu(false);
            // 延迟隐藏，检查是否移动到其他相关区域
            setTimeout(() => {
              if (
                !isMouseOverPermissionButton &&
                !isMouseOverPermissionDetail
              ) {
                setIsPermissionMenuVisible(false);
                setIsPermissionDetailVisible(false);
              }
            }, 200);
          }}
        >
          {/* 设置全员权限 - 添加向右三角形图标和悬浮事件 */}
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#333'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              handleAllPermissionHover(e);
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
              handleAllPermissionLeave();
            }}
          >
            <span>设置全员权限</span>
            <CaretRightOutlined style={{color: '#999', fontSize: '12px'}} />
          </div>

          {/* 为指定成员添加额外权限 */}
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#333'
            }}
            onClick={e => {
              e.stopPropagation();
              console.log('为指定成员添加额外权限被点击');
              // 这里可以打开成员选择器
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span>为指定成员添加额外权限</span>
          </div>
        </div>
      )}

      {/* 权限详情卡片 - 悬浮显示 */}
      {isPermissionDetailVisible && (
        <div
          ref={permissionDetailRef}
          data-permission-detail="true"
          style={{
            position: 'fixed',
            top: cardPosition.top + 'px',
            left: permissionDetailPosition.left + 'px',
            zIndex: 100001,
            width: '280px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #d9d9d9',
            padding: '12px 0'
          }}
          onClick={e => e.stopPropagation()}
          onMouseEnter={() => {
            setIsMouseOverPermissionDetail(true);
            setIsPermissionDetailVisible(true);
          }}
          onMouseLeave={() => {
            setIsMouseOverPermissionDetail(false);
            // 延迟隐藏，检查是否回到菜单区域
            setTimeout(() => {
              setIsPermissionDetailVisible(false);
            }, 200);
          }}
        >
          <div
            style={{
              padding: '0 16px 8px',
              fontSize: '14px',
              fontWeight: 'bold',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            设置全员权限
          </div>

          {permissionOptions.map((option: any) => {
            // 检查权限是否匹配的函数
            const isPermissionMatched = () => {
              if (
                !focusSheetPermissions ||
                !Array.isArray(focusSheetPermissions)
              ) {
                return false;
              }

              // 如果 required 为空数组（无权限），检查 focusSheetPermissions 是否也为空或只包含空值
              if (option.required.length === 0) {
                return (
                  focusSheetPermissions.length === 0 ||
                  focusSheetPermissions.every(
                    permission => !permission || permission.trim() === ''
                  )
                );
              }

              // 检查 focusSheetPermissions 是否包含 option.required 中的所有元素
              // 并且长度相等（确保完全匹配）
              return (
                option.required.length === focusSheetPermissions.length &&
                option.required.every((required: any) =>
                  focusSheetPermissions.includes(required)
                ) &&
                focusSheetPermissions.every(permission =>
                  option.required.includes(permission)
                )
              );
            };

            const isSelected = activeView === 'sheet' ? isPermissionMatched() : option.value === focusDashboardInfo?.permission;
            
            return (
              <div
                key={option.value}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  color: '#333',
                  backgroundColor: isSelected ? '#e6f7ff' : 'transparent'
                }}
                onClick={e => { onSetPermissionClick(e, option.value) }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = isSelected
                    ? '#e6f7ff'
                    : '#f5f5f5';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = isSelected
                    ? '#e6f7ff'
                    : 'transparent';
                }}
              >
                <div style={{flex: 1}}>
                  <div style={{fontWeight: 'bold', marginBottom: '4px'}}>
                    {option.label}
                  </div>
                  <div
                    style={{fontSize: '12px', color: '#666', lineHeight: '1.4'}}
                  >
                    {option.description}
                  </div>
                </div>
                {isSelected && (
                  <CheckOutlined
                    style={{
                      color: '#1890ff',
                      fontSize: '16px',
                      marginLeft: '8px',
                      marginTop: '2px'
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 仪表盘操作卡片 */}
      {isDashboardCardVisible &&
        focusDashboardInfo?.createUserId === userId && (
          <div
            ref={sheetCardRef}
            style={{
              position: 'absolute',
              top: cardPosition.top + 'px',
              left: cardPosition.left + 'px',
              zIndex: 99999,
              width: '200px',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid #d9d9d9',
              padding: '8px 0'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* 权限设置 - 添加向右三角形图标 */}
            <div
              data-permission-button="true"
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#333'
              }}
              onClick={(e) => handlePermissionClick(e, 'dashboard')}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <LockOutlined style={{marginRight: '8px'}} />
                设置工作表权限
              </div>
              <CaretRightOutlined style={{color: '#999', fontSize: '12px'}} />
            </div>
            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#333'
              }}
              onClick={e => {
                e.stopPropagation();
                onRenameDashboard();
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <EditOutlined style={{marginRight: '8px'}} />
              重命名
            </div>

            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#ff4d4f',
                borderTop: '1px solid #f0f0f0',
                marginTop: '4px'
              }}
              onClick={e => {
                e.stopPropagation();
                if (focusDashboardInfo?.id) {
                  onDeleteDashboard(focusDashboardInfo.id);
                }
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#fff2f0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <DeleteOutlined style={{marginRight: '8px'}} />
              删除
            </div>
          </div>
        )}
    </>
  );
};
